<template>
  <div class="layout">
    <header class="topbar">
      <div class="topbar__inner">
        <div class="system-title">{{ systemTitle }}</div>
        <div class="user-box">
          <el-dropdown @command="onUserCommand">
            <span class="user-trigger">
              <el-avatar size="small" icon="UserFilled" />
              <span class="user-name">{{ displayUserName }}</span>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </header>
    <div class="layout-body">
      <div class="layout-shell">
      <div class="layout-main">
    <aside class="sidebar panel-card">
      <div class="nav-section">
        <div
          v-if="isSuperAdmin"
          class="nav-item"
          :class="{active: activeL1 && activeL1.id === 0}"
          @click="selectHome"
        >
          <span class="icon">🏠</span><span>首页</span>
        </div>
        <div
          v-if="specialNavItem"
          class="nav-item"
          :class="{active: activeL1 && activeL1.id === specialNavItem.id}"
          @click="selectSpecial"
        >
          <span class="icon">{{ specialNavItem.meta?.icon || '⭐️' }}</span>
          <span>{{ specialNavItem.name }}</span>
        </div>
        <div class="nav-item" :class="{active: activeL1 && activeL1.id === -1}" @click="selectSearch">
          <span class="icon">🔍</span><span>搜索</span>
        </div>
      </div>

      <div class="nav-group">
        <div class="group-title">模块</div>
        <el-menu
          v-if="visibleMenus.length"
          class="menu-tree"
          :default-active="activeIndex || 'menu-root'"
          :unique-opened="false"
          :default-openeds="[]"
        >
          <MenuNode
            v-for="(node, idx) in visibleMenus"
            :key="node?.id || `menu-${idx}`"
            :node="node"
            :onLeafClick="handleLeafList"
          />
        </el-menu>
        <div v-else class="menu-empty">加载菜单中...</div>
      </div>
    </aside>
    <main class="content">
        <section class="content-panel panel-card">
          <div class="crumb-row">
            <template v-for="(b, idx) in crumbs" :key="idx">
              <a
                v-if="isCrumbClickable(b, idx)"
                :class="{ 'crumb-current': idx === crumbs.length - 1 }"
                @click.prevent="goCrumb(b.path)"
              >{{ b.label }}</a>
              <span v-else :class="{ 'crumb-current': idx === crumbs.length - 1 }">{{ b.label }}</span>
              <span v-if="idx < crumbs.length - 1" class="crumb-sep">/</span>
            </template>
          </div>
          <div class="content-panel__body">
            <template v-if="sideSubList.length">
              <div class="level4-panel">
                <div class="level4-title">{{ level4Title }}</div>
                <div class="level4-grid">
                  <button
                    v-for="item in sideSubList"
                    :key="item.id"
                    class="level4-item"
                    @click="navigateTo(item)">
                    {{ item.name }}
                  </button>
                </div>
              </div>
            </template>
            <router-view v-else :key="route.fullPath" />
          </div>
        </section>
    </main>
      </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, provide, defineComponent, h, resolveComponent, watch } from 'vue'
import * as ElIcons from '@element-plus/icons-vue'
import { useRouter, useRoute } from 'vue-router'
import axios from 'axios'
import { clearLoginCache, getDisplayUserName, getUserId, isSuperAdmin as checkSuperAdmin } from '../utils/auth'
import { loadSiteConfig } from '../utils/siteConfig'

const systemTitle = ref(process.env.VUE_APP_TITLE || '应用系统')

const menus = ref([])
const activeL1 = ref(null)
const activeL2 = ref(null)
const activeL3 = ref(null)
const activeL4 = ref(null)
const router = useRouter()
const route = useRoute()
const userId = getUserId()
const displayUserName = computed(() => getDisplayUserName())
const isSuperAdmin = computed(() => checkSuperAdmin())

const specialNavItem = ref(null)
const level1 = computed(() => menus.value || [])
const level2Tabs = computed(() => (activeL1.value && activeL1.value.children) || [])
const level3List = computed(() => (activeL2.value && activeL2.value.children) || [])
const level4List = computed(() => (activeL3.value && activeL3.value.children) || [])
const isVisibleNode = (n) => n && n.type !== 'F' && n.type !== 'B'
const visibleMenus = computed(() => (menus.value || []).filter((n) => n && isVisibleNode(n)))
const manualActive = ref('')
const sideSubList = ref([])
const level4Title = ref('')
const handleLeafList = (node, children) => {
  if (!node) return
  sideSubList.value = (children || []).filter((c) => isVisibleNode(c))
  manualActive.value = String(node.id ?? node.path ?? node.name ?? '') || 'menu-leaf'
  level4Title.value = node.name || '下级菜单'
}
const navigateTo = (item) => {
  if (item && item.path) {
    router.push(normalizeAdminPath(item.path))
  }
}
const findIdByPath = (nodes = [], target) => {
  for (const n of nodes) {
    if (!isVisibleNode(n)) continue
    const selfPath = normalizeAdminPath(n.path)
    if (selfPath === target) return String(n.id ?? selfPath)
    if (n.children && n.children.length) {
      const child = findIdByPath(n.children, target)
      if (child) return child
    }
  }
  return ''
}
const activeIndex = computed(() => {
  if (manualActive.value) return manualActive.value
  const current = route.path || '/admin'
  return findIdByPath(visibleMenus.value, current) || normalizeAdminPath(current) || 'menu-root'
})
const isMenuPage = computed(() => route.name === 'menu')

watch(
  () => route.path,
  () => {
    manualActive.value = ''
    sideSubList.value = []
    level4Title.value = ''
  }
)

const normalizeAdminPath = (p) => {
  if (!p) return ''
  if (p.startsWith('/admin')) return p
  if (p.startsWith('/')) return `/admin${p}`
  return `/admin/${p}`
}

/** 仅 type=C 且自身绑定了 path 的菜单才可作为面包屑跳转目标 */
const resolveCrumbPath = (node) => {
  if (!node || !isVisibleNode(node)) return ''
  if (node.type === 'C' && node.path) return normalizeAdminPath(node.path)
  return ''
}

const INVALID_CRUMB_PATHS = new Set(['/admin/menu', '/admin/home'])

const isCrumbClickable = (item, idx) => {
  if (!item?.path || idx >= crumbs.value.length - 1) return false
  if (typeof item.path === 'object') return true
  const path = normalizeAdminPath(item.path)
  return path && !INVALID_CRUMB_PATHS.has(path)
}

const goCrumb = (path) => {
  if (!path) return
  sideSubList.value = []
  manualActive.value = ''
  level4Title.value = ''
  if (typeof path === 'object') {
    router.push(path)
    return
  }
  const target = normalizeAdminPath(path)
  if (!target || INVALID_CRUMB_PATHS.has(target)) return
  router.push(target)
}

const buildMetaFormConfigListQuery = (query = {}) => {
  const listQuery = {}
  if (query.schemaCode) listQuery.schemaCode = String(query.schemaCode)
  if (query.formCode) listQuery.formCode = String(query.formCode)
  if (query.formName) listQuery.formName = String(query.formName)
  return listQuery
}

const buildMetaArchitectureBase = () => [
  { label: '系统设置' },
  { label: '数据架构配置' },
]

const buildMetaFormTemplateListCrumb = () => ({
  label: '字段配置表单',
  path: '/admin/meta/form',
})

const buildMetaFormConfigListCrumb = (query = {}) => {
  const listQuery = buildMetaFormConfigListQuery(query)
  const hasQuery = Object.keys(listQuery).length > 0
  return {
    label: '表单配置',
    path: hasQuery
      ? { path: '/admin/meta-form-config-list', query: listQuery }
      : '/admin/meta-form-config-list',
  }
}

/** /admin/meta/form — 配置表单列表（选表单模板） */
const buildMetaFormCrumbs = () => [
  ...buildMetaArchitectureBase(),
  { label: '字段配置表单' },
]

/** /admin/meta-form-config-list — 表单配置（选配置模板） */
const buildMetaFormConfigListCrumbs = () => [
  ...buildMetaArchitectureBase(),
  buildMetaFormTemplateListCrumb(),
  { label: '表单配置' },
]

/** /admin/meta-form-config?configId= — 编辑表单（字段配置详情） */
const buildMetaFormConfigEditorCrumbs = () => {
  const q = route.query || {}
  return [
    ...buildMetaArchitectureBase(),
    buildMetaFormTemplateListCrumb(),
    buildMetaFormConfigListCrumb(q),
    { label: q.configName ? String(q.configName) : '编辑表单' },
  ]
}

const findCrumbsByPath = (nodes = [], targetPath) => {
  const normalizedTarget = normalizeAdminPath(targetPath)
  for (const n of nodes) {
    if (!isVisibleNode(n)) continue
    const selfPath = n.path ? normalizeAdminPath(n.path) : ''
    if (selfPath && selfPath === normalizedTarget) {
      return [{ label: n.name || n.title || n.path }]
    }
    if (n.children && n.children.length) {
      const child = findCrumbsByPath(n.children, targetPath)
      if (child.length) {
        const parentPath = resolveCrumbPath(n)
        const parentCrumb = { label: n.name || n.title || n.path }
        if (parentPath) parentCrumb.path = parentPath
        return [parentCrumb, ...child]
      }
    }
  }
  return []
}

const crumbs = computed(() => {
  const sanitize = (items = []) => {
    const list = Array.isArray(items) ? items : []
    return list.map((item, idx) => {
      if (!item?.path) return item
      if (idx >= list.length - 1) {
        const { path: _drop, ...rest } = item
        return rest
      }
      if (typeof item.path === 'object') return item
      const path = normalizeAdminPath(item.path)
      if (INVALID_CRUMB_PATHS.has(path)) {
        const { path: _drop, ...rest } = item
        return rest
      }
      return { ...item, path }
    })
  }
  if (route.name === 'metaForm') {
    return buildMetaFormCrumbs()
  }
  if (route.name === 'metaFormConfigList') {
    return buildMetaFormConfigListCrumbs()
  }
  if (route.name === 'metaFormConfig' && route.query?.configId) {
    return buildMetaFormConfigEditorCrumbs()
  }
  if (route.meta?.overrideBreadcrumbs) {
    return sanitize(route.meta?.breadcrumbs || [])
  }
  if (isMenuPage.value) {
    return [{ label: '导航' }, { label: activeL1.value ? activeL1.value.name : '首页' }]
  }
  const menuCrumbs = findCrumbsByPath(menus.value, route.path)
  if (menuCrumbs.length) return menuCrumbs
  return sanitize(route.meta?.breadcrumbs || [])
})

const selectHome = () => {
  activeL1.value = { id: 0, name: '首页', children: [] }
  activeL2.value = null
  router.push('/admin/home')
}

const selectSearch = () => {
  activeL1.value = { id: -1, name: '搜索', children: [] }
  activeL2.value = null
}

const selectSpecial = () => {
  if (!specialNavItem.value) return
  activeL1.value = specialNavItem.value
  activeL2.value = specialNavItem.value.children && specialNavItem.value.children.length ? specialNavItem.value.children[0] : null
  activeL3.value = null
  activeL4.value = null
  const target = specialNavItem.value.path || (specialNavItem.value.children && specialNavItem.value.children[0] && specialNavItem.value.children[0].path)
  if (target) {
    router.push(normalizeAdminPath(target))
  }
}

const selectL1 = (item) => {
  activeL1.value = item
  const firstChild = (item.children && item.children[0]) || null
  activeL2.value = firstChild
  activeL3.value = firstChild && firstChild.children ? firstChild.children[0] || null : null
  activeL4.value = activeL3.value && activeL3.value.children ? activeL3.value.children[0] || null : null
}

const selectL2 = (item) => {
  activeL2.value = item
  activeL3.value = item && item.children ? item.children[0] || null : null
  activeL4.value = activeL3.value && activeL3.value.children ? activeL3.value.children[0] || null : null
  if (item && item.type === 'C' && item.path) {
    router.push(normalizeAdminPath(item.path))
  }
}

const selectL3 = (item) => {
  activeL3.value = item
  activeL4.value = item && item.children ? item.children[0] || null : null
  if (item && item.type === 'C' && item.path) {
    router.push(normalizeAdminPath(item.path))
    manualActive.value = ''
    sideSubList.value = []
  } else if (item && item.children && item.children.length) {
    sideSubList.value = item.children.filter((c) => isVisibleNode(c))
    manualActive.value = String(item.id ?? item.path ?? '')
  }
}

const selectL4 = (item) => {
  activeL4.value = item
  if (item && item.type === 'C' && item.path) {
    router.push(normalizeAdminPath(item.path))
  }
}

const findSpecialNode = (nodes = []) => {
  for (const node of nodes) {
    if (!node) continue
    const isSpecial = Boolean(node.meta?.specialNav) || node.permission === 'specialNav'
    if (isSpecial) {
      return {
        ...node,
        meta: { ...(node.meta || {}), specialNav: true },
      }
    }
    if (node.children && node.children.length) {
      const child = findSpecialNode(node.children)
      if (child) return child
    }
  }
  return null
}

const filterOutSpecial = (nodes = [], special) => {
  if (!special) return nodes
  return (nodes || []).filter((node) => {
    if (!node) return false
    if (node.id === special.id) return false
    if (node.children && node.children.length) {
      node = { ...node, children: filterOutSpecial(node.children, special) }
    }
    return true
  })
}

const loadMenus = async () => {
  try {
    const res = await axios.get('/api/menus/tree', { params: { adminId: userId || undefined } })
    if (res.data && res.data.data) {
      const prefixPath = (p) => {
        if (!p) return undefined
        if (p.startsWith('/admin')) return p
        if (p.startsWith('/')) return `/admin${p}`
        return `/admin/${p}`
      }

      const sortTree = (nodes = []) => {
        const clean = (nodes || []).filter((n) => n && String(n.id) !== '0')
        return clean
          .sort((a, b) => {
            const sa = Number(a.sort ?? 0)
            const sb = Number(b.sort ?? 0)
            if (sa !== sb) return sa - sb
            return Number(a.id ?? 0) - Number(b.id ?? 0)
          })
          .map((n) => ({
            ...n,
            path: prefixPath(n.path),
            children: n.children && n.children.length ? sortTree(n.children) : [],
          }))
      }
      const sorted = sortTree(res.data.data)
      const special = findSpecialNode(sorted)
      menus.value = special ? filterOutSpecial(sorted, special) : sorted
      specialNavItem.value = special || null
      if (menus.value.length) {
        selectL1(menus.value[0])
      }
    }
  } catch (e) {
    console.error('加载菜单失败', e)
  }
}

const loadSiteName = async () => {
  try {
    const config = await loadSiteConfig()
    if (config?.siteName) systemTitle.value = config.siteName
  } catch (e) {
    // keep default
  }
}

const onSiteConfigUpdated = (event) => {
  const siteName = event?.detail?.siteName
  if (siteName) systemTitle.value = siteName
}

onMounted(() => {
  window.addEventListener('bymy:site-config-updated', onSiteConfigUpdated)
  loadSiteName()
  loadMenus()
})

onUnmounted(() => {
  window.removeEventListener('bymy:site-config-updated', onSiteConfigUpdated)
})

const onUserCommand = (cmd) => {
  if (cmd === 'logout') {
    clearLoginCache()
    router.replace('/admin/login')
  }
}

provide('menuContext', {
  level1,
  activeL1,
  activeL2,
  activeL3,
  activeL4,
  level2Tabs,
  level3Getter: (l2) => (l2 && l2.children) || [],
  selectL1,
  selectL2,
  selectL3,
  selectL4,
})

const MenuNode = defineComponent({
  name: 'MenuNode',
  props: {
    node: { type: Object, required: true },
    depth: { type: Number, default: 1 },
    onLeafClick: { type: Function, default: null },
  },
  setup(props) {
    const normalizeAdminPath = (p) => {
      if (!p) return '/admin/menu'
      if (p.startsWith('/admin')) return p
      if (p.startsWith('/')) return `/admin${p}`
      return `/admin/${p}`
    }

    if (!props.node) return () => null

    const children = computed(() => (props.node.children || []).filter((c) => c && isVisibleNode(c)))
    const hasChildren = computed(() => children.value.length > 0)
    const indexKey = computed(() => {
      const key = props.node.id ?? props.node.path ?? props.node.name
      if (key !== undefined && key !== null && key !== '') {
        return String(key)
      }
      return `menu-depth-${props.depth}-unnamed`
    })
    const iconComp = computed(() => {
      const key = props.node.icon || props.node.meta?.icon
      return key && ElIcons[key] ? ElIcons[key] : null
    })
    const renderIcon = () => {
      if (iconComp.value) {
        return h(iconComp.value, { class: 'icon-comp' })
      }
      return h('span', { class: 'icon' }, hasChildren.value ? '📁' : '📄')
    }

    return () => {
      if (!props.node || !isVisibleNode(props.node)) return null
      if (!props.node.name && !props.node.path && !hasChildren.value) return null
      const ElSubMenu = resolveComponent('el-sub-menu')
      const ElMenuItem = resolveComponent('el-menu-item')

      const canShowChildren = hasChildren.value && props.depth < 3

      if (canShowChildren) {
        return h(
          ElSubMenu,
          { index: indexKey.value, 'popper-class': 'menu-popper' },
          {
            title: () => [
              renderIcon(),
              h('span', null, props.node.name),
            ],
            default: () => children.value.map((c, idx) => h(MenuNode, {
              node: c,
              key: c.id ?? c.path ?? `${indexKey.value}-child-${idx}`,
              depth: props.depth + 1,
              onLeafClick: props.onLeafClick,
            })),
          }
        )
      }
      const handleClick = () => {
        const node = props.node
        if (node?.type === 'C' && node.path) {
          router.push(normalizeAdminPath(node.path))
          return
        }
        if (props.depth === 3 && hasChildren.value && props.onLeafClick) {
          props.onLeafClick(node, children.value)
          return
        }
        if (node?.path) {
          router.push(normalizeAdminPath(node.path))
          return
        }
        if (node?.redirect) {
          router.push(normalizeAdminPath(node.redirect))
        }
      }
      return h(
        ElMenuItem,
        { index: indexKey.value, onClick: handleClick },
        () => [
          renderIcon(),
          h('span', null, props.node.name),
        ]
      )
    }
  },
})
</script>

<style scoped>
.layout {
  display: flex;
  flex-direction: column;
  height: 100vh;
  min-height: 0;
  overflow: hidden;
  background: var(--bymy-color-bg-page);
  box-sizing: border-box;
}
.layout-body {
  flex: 1;
  display: flex;
  justify-content: center;
  min-height: 0;
  overflow: hidden;
  padding: var(--bymy-layout-gap);
  box-sizing: border-box;
}
.layout-shell {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: var(--bymy-layout-shell-max-width);
  height: 100%;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
}
.layout-main {
  display: flex;
  align-items: stretch;
  gap: var(--bymy-layout-gap);
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  box-sizing: border-box;
}
.panel-card {
  background: var(--bymy-color-bg-card);
  border: none;
  border-radius: var(--bymy-radius-lg);
  box-shadow: var(--bymy-shadow-float);
  box-sizing: border-box;
  min-height: 0;
}
.sidebar {
  width: var(--bymy-sidebar-width);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 8px 0 12px;
}
.nav-section {
  padding: 4px 12px 8px;
}
.nav-group {
  padding: 0 8px 12px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}
.nav-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border-radius: 8px;
  cursor: pointer;
  color: #374151;
  font-size: 14px;
  transition: color 0.15s ease, background-color 0.15s ease;
}
.menu-tree {
  --el-menu-bg-color: transparent;
  --el-menu-text-color: #111827;
  --el-menu-active-color: #2563eb;
  border-right: none;
  background: transparent;
}
.menu-tree :deep(.el-menu-item.is-active),
.menu-tree :deep(.el-sub-menu__title.is-active) {
  background: transparent;
  color: var(--bymy-color-primary);
  font-weight: 600;
}
.menu-tree :deep(.el-menu-item:hover),
.menu-tree :deep(.el-sub-menu__title:hover) {
  background: rgba(15, 23, 42, 0.04);
  color: #111827;
}
.menu-tree :deep(.el-menu-item),
.menu-tree :deep(.el-sub-menu__title) {
  height: 38px;
  line-height: 38px;
}
.menu-tree :deep(.el-sub-menu .el-menu) {
  background: transparent;
}
.menu-popper {
  background: #fff;
}
.menu-popper .el-menu {
  background: #fff;
}
.menu-popper .el-menu-item.is-active {
  background: rgba(37, 99, 235, 0.12);
  color: #2563eb;
}
.nav-item.active {
  background: transparent;
  color: var(--bymy-color-primary);
  font-weight: 600;
}
.nav-item:hover {
  background: rgba(15, 23, 42, 0.04);
  color: #111827;
}
.icon { width: 16px; text-align: center; }
.icon-comp {
  width: 16px;
  height: 16px;
  font-size: 16px;
  flex-shrink: 0;
}
:deep(.icon-comp) {
  width: 16px;
  height: 16px;
  font-size: 16px;
}
:deep(.icon-comp svg) {
  width: 16px;
  height: 16px;
}
:deep(.el-menu-item),
:deep(.el-sub-menu__title) {
  display: flex;
  align-items: center;
  gap: 6px;
}
:deep(.el-menu-item .icon-comp),
:deep(.el-sub-menu__title .icon-comp),
.icon {
  margin-right: 4px;
}
.group-title {
  color: var(--bymy-color-text-muted);
  font-weight: 600;
  font-size: 12px;
  margin: 8px 12px 6px;
  padding-left: 4px;
}
.content {
  flex: 1;
  display: flex;
  flex-direction: column;
  text-align: left;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}
.content-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.content-panel__body {
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 0 var(--bymy-gap-md) var(--bymy-gap-md);
  box-sizing: border-box;
}
.content-panel__body :deep(.page-wrapper) {
  flex: 1;
  min-height: 0;
}
.content-panel__body :deep(.dashboard-page) {
  flex: 1;
  min-height: 0;
}
.content-panel__body :deep(.list-shell.list-shell--embedded) {
  border: none;
  box-shadow: none;
  background: transparent;
  padding: 0;
  border-radius: 0;
}
.topbar {
  flex-shrink: 0;
  background: var(--bymy-color-bg-card);
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: var(--bymy-shadow-header);
}
.topbar__inner {
  height: var(--bymy-header-height);
  min-height: var(--bymy-header-height);
  max-width: var(--bymy-layout-shell-max-width);
  margin: 0 auto;
  padding: 0 var(--bymy-layout-padding-x);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-sizing: border-box;
}
.system-title {
  font-size: 17px;
  font-weight: 700;
  line-height: 1;
  color: #111827;
  letter-spacing: 0.2px;
}
.crumb-row {
  padding: 16px var(--bymy-gap-lg) 14px;
  color: var(--bymy-color-text-secondary);
  font-size: 13px;
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-shrink: 0;
  background: var(--bymy-color-bg-card);
}
.crumb-sep {
  color: #d1d5db;
  font-size: 12px;
  user-select: none;
}
.crumb-current {
  color: #111827;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
  letter-spacing: 0.2px;
}
.crumb-row a {
  color: var(--bymy-color-text-secondary);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: color 0.15s ease;
  cursor: pointer;
}
.crumb-row a.crumb-current {
  color: #111827;
  cursor: default;
}
.level4-panel {
  background: #f9fafb;
  border: none;
  border-radius: var(--bymy-radius-md);
  padding: 12px 16px;
  margin: 0 0 16px;
}
.level4-title {
  font-weight: 700;
  color: #111827;
  margin-bottom: 10px;
}
.level4-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px;
}
.level4-item {
  border: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 10px;
  padding: 10px 12px;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
}
.level4-item:hover {
  border-color: #2563eb;
  background: rgba(37, 99, 235, 0.05);
  color: #2563eb;
}
.crumb-row a:not(.crumb-current):hover {
  color: #111827;
  text-decoration: none;
}
.user-box { display: flex; align-items: center; }
.user-trigger { display: inline-flex; align-items: center; gap: 8px; cursor: pointer; padding: 6px 10px; border-radius: 16px; background: #f3f4f6; }
.user-name { color: #111827; font-weight: 600; font-size: 14px; }
</style>
